import { ChevronDownIcon, WarningTwoIcon } from "@chakra-ui/icons";
import {
  Button,
  Checkbox,
  Input,
  Menu,
  MenuButton,
  MenuList,
  useDisclosure,
  VStack,
  Text,
  Tooltip,
  HStack,
  Portal,
  Box,
} from "@chakra-ui/react";
import React, { useState, useMemo } from "react";

interface ColumnValuesProps {
  contextValues?: string[]; 
  selectedOptions: string[]; 
  setSelectedOptions: (values: string[]) => void; 
  errorMsg?: string;
}

const ColumnValues: React.FC<ColumnValuesProps> = ({
  contextValues,
  selectedOptions,
  setSelectedOptions,
  errorMsg,
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [searchTerm, setSearchTerm] = useState("");
  
  // Filter contextValues based on search term
  const filteredValues = useMemo(() => {
    if (!contextValues) return [];
    if (!searchTerm.trim()) return contextValues;
    
    return contextValues.filter(value => 
      value !== null && value.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [contextValues, searchTerm]);
  
  // Function to handle select/deselect of multiple values
  const handleToggle = (value: string) => {
    const currentIndex = selectedOptions.indexOf(value);
    let newSelected = [...selectedOptions];

    if (currentIndex === -1) {
      newSelected.push(value);
    } else {
      newSelected.splice(currentIndex, 1);
    }
    setSelectedOptions(newSelected);
  };

  // Clear search when popover closes
  const handleClose = () => {
    setSearchTerm("");
    onClose();
  };

  return (
    <Menu isOpen={isOpen} onClose={handleClose} closeOnSelect={false}>
      <MenuButton
        as={Button}
        size="sm"
        color="fg.default"
        bg="white"
        maxW="100%"
        width="100%"
        rightIcon={<ChevronDownIcon />}
        fontSize="sm"
        border="1px"
        borderColor="purple.700"
        borderRadius="md"
        fontWeight="semibold"
        _hover={{ bg: "purple.50" }}
        textAlign="left"
        onClick={onOpen}
        justifyContent="space-between"
      >
        {selectedOptions.length > 0
          ? `(${selectedOptions.length} ${selectedOptions.length === 1 ? "Value" : "Values"}) - ${selectedOptions.join(", ")}`
          : "Select Options"}
      </MenuButton>

      <Portal>
        <MenuList maxH="250px" overflowY="auto" zIndex={1600} p={0} minWidth="300px">
          <Box>
            {/* Search Input */}
            <HStack justifyContent="space-between">
              <Input
                placeholder="Search options..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                size="sm"
                m={2}
                mb={1}
                borderColor="gray.300"
                _focus={{ borderColor: "purple.400", boxShadow: "0 0 0 1px purple.400" }}
                width="95%"
              />
              {errorMsg && (
                <Tooltip
                  label={errorMsg}
                  placement="right"
                  hasArrow
                  bg="red.400"
                  color="white"
                  borderRadius="md"
                >
                  <WarningTwoIcon color="red.500" ml={1} mr={2} />
                </Tooltip>
              )}
            </HStack>
            {/* Scrollable Options List */}
            <VStack 
              align="start" 
              spacing={0} 
              maxHeight="180px" 
              overflowY="auto"
              pt={1}
            >
            {filteredValues?.map((option, index) => (
              <Checkbox
                key={index}
                isChecked={selectedOptions.includes(option)}
                onChange={() => handleToggle(option)}
                colorScheme="purple"
                _hover={{cursor:"pointer" ,bg:"gray.200"}}
                p={2}
                width="100%" // Ensures checkboxes take up full width
                height="100%"
              >
                <Text fontSize="sm" fontWeight="600" mb={0}>{option}</Text>
              </Checkbox>
            ))}
            {filteredValues.length === 0 && searchTerm.trim() && (
              <div style={{ padding: "8px 16px", color: "gray", fontSize: "14px" }}>
                No options found
              </div>
            )}
          </VStack>
          </Box>
        </MenuList>
      </Portal>
    </Menu>
  );
};

export default ColumnValues;
